import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages the GVS cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #openRequest = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#closeImmediately();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      'click',
      this.#handleNavigationClick,
      true
    );

    window.addEventListener(
      'pagehide',
      this.#handlePageLifecycle
    );

    window.addEventListener(
      'pageshow',
      this.#handlePageLifecycle
    );

    this.dataset.inputModality = 'pointer';
    document.addEventListener('pointerdown', this.#handlePointerInput, true);
    document.addEventListener('keydown', this.#handleKeyboardInput, true);
    this.refs.dialog.addEventListener('close', this.#syncTriggerState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      'click',
      this.#handleNavigationClick,
      true
    );

    window.removeEventListener(
      'pagehide',
      this.#handlePageLifecycle
    );

    window.removeEventListener(
      'pageshow',
      this.#handlePageLifecycle
    );

    document.removeEventListener('pointerdown', this.#handlePointerInput, true);
    document.removeEventListener('keydown', this.#handleKeyboardInput, true);
    this.refs.dialog.removeEventListener('close', this.#syncTriggerState);
    this.#openRequest += 1;
  }

  #handleCartAdd = async (event) => {
    if (!this.hasAttribute('auto-open') || event.detail?.data?.didError) return;

    const request = ++this.#openRequest;
    const sourceDialog = event.target instanceof Element
      ? event.target.closest('dialog[open][scroll-lock]')
      : null;

    // Quick Add emits the cart event before its closing animation has
    // completed. Wait for the originating overlay to finish closing before
    // opening the basket so two modal top layers never overlap.
    if (sourceDialog instanceof HTMLDialogElement && sourceDialog !== this.refs.dialog) {
      await closeOwningOverlay(sourceDialog);
    }

    if (request !== this.#openRequest || !this.isConnected) return;
    this.open();
  };

  #handlePageLifecycle = () => {
    this.#closeImmediately();
  };

  #handlePointerInput = () => {
    this.dataset.inputModality = 'pointer';
  };

  #handleKeyboardInput = (event) => {
    if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') {
      this.dataset.inputModality = 'keyboard';
    }
  };

  /**
   * Close the drawer synchronously before following an internal link so a
   * browser-restored page cannot reopen the previous basket state.
   *
   * @param {MouseEvent} event
   */
  #handleNavigationClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!link || !this.contains(link)) return;

    this.#closeImmediately();
  };

  open(opener) {
    this.showDialog(opener instanceof Event ? opener : this.refs.cartTrigger);
    this.#syncTriggerState();

    requestAnimationFrame(() => {
      const scrollRoot = this.querySelector('[data-cart-drawer-scroll-root]');
      const itemScroll = this.querySelector('[data-cart-drawer-scroll]');
      const rootOverflow = scrollRoot instanceof HTMLElement
        ? window.getComputedStyle(scrollRoot).overflowY
        : '';
      const scrollArea = rootOverflow === 'auto' || rootOverflow === 'scroll'
        ? scrollRoot
        : itemScroll;

      if (scrollArea instanceof HTMLElement) {
        scrollArea.scrollTop = 0;
      }
    });

    /**
     * Close the cart drawer when the instalments CTA is clicked to avoid
     * overlapping dialogs.
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const instalmentsContent = document.querySelector(
          'shopify-payment-terms'
        )?.shadowRoot;

        const cta = instalmentsContent?.querySelector(
          '#shopify-installments-cta'
        );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          { once: true }
        );
      });
  }

  close() {
    this.closeDialog();
  }

  #syncTriggerState = () => {
    const trigger = this.refs.cartTrigger;
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', String(Boolean(this.refs.dialog?.open)));
    }
  };

  /** Immediately clear an open/restored dialog without waiting for motion. */
  #closeImmediately() {
    const { dialog } = this.refs;

    if (!dialog?.open) {
      this.#syncTriggerState();
      return;
    }

    dialog.classList.remove('dialog-closing');
    dialog.close();

    const otherOpenScrollLock = Array.from(
      document.querySelectorAll('[scroll-lock][open]')
    ).some((element) => element !== dialog);

    if (!otherOpenScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    this.#syncTriggerState();
  }
}

/**
 * Close the component that owns an already-open dialog and wait for its
 * animation. Fall back to the native close method for non-GVS dialogs.
 * @param {HTMLDialogElement} dialog
 * @returns {Promise<void>}
 */
async function closeOwningOverlay(dialog) {
  const owner = dialog.parentElement;
  const closeMethod = owner && typeof owner.closeDialog === 'function'
    ? owner.closeDialog
    : owner && typeof owner.close === 'function'
      ? owner.close
      : null;

  if (closeMethod) {
    await closeMethod.call(owner);
    return;
  }

  dialog.classList.remove('dialog-closing', 'dialog--closed');
  if (dialog.open) dialog.close();
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}